"use client";

import { useState, useEffect, useRef } from "react";
import { useSearchParams, useRouter } from "next/navigation";
import { Search, Check, Building2, Loader2, Save, X, Settings, Eye, Plus, ChevronDown, ChevronUp, FileText, Upload, Mail, AlertCircle, Link2, Map } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/app/_module/components/ui/card";
import { Button } from "@/app/_module/components/ui/button";
import { Input } from "@/app/_module/components/ui/input";
import { ScrollArea } from "@/app/_module/components/ui/scroll-area";
import { Badge } from "@/app/_module/components/ui/badge";
import { Checkbox } from "@/app/_module/components/ui/checkbox";
import { Alert, AlertDescription } from "@/app/_module/components/ui/alert";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/app/_module/components/ui/tabs";
import { Dialog, ResponsiveDialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/app/_module/components/ui/dialog";
import { Textarea } from "@/app/_module/components/ui/textarea";
import { Label } from "@/app/_module/components/ui/label";
import { useToast } from "@/app/_module/hooks/use-toast";
import { useUserContext } from "@/app/_module/store/contexts/userContext";
import { getAdminAccess } from "@/app/_module/utils/admin-access";
import api from "@/app/_module/services/api";
import { getApiUrl } from "@/app/_module/services/config";
import { handleApiCall } from "@/app/_module/utils/apiResponseHandler";
import { useBranding } from "@/app/_module/store/contexts/brandingContext";
import AgencyTokenManager from "@/app/_module/services/agencyTokenManager";
import { oauthConnectionService } from "@/app/_module/services/oauthConnectionService";
import type { EmailOAuthConnection } from "@/app/_module/types/email-oauth";
import PolicyMapInline from "./PolicyMapModal";

// Request type for carrier requests
type CarrierRequestType = "add_carrier" | "underwriting_data";

interface Carrier {
  id: string;
  slug: string;
  name: string;
  isActive: boolean;
  logoUrl?: string;
}

interface AgencyCarrier {
  agencyId: string;
  carrierId: string;
  carrierSlug: string;
  carrierName: string;
  isEnabled: boolean;
  displayOrder: number;
  isDefault: boolean;
  commissionRate?: number;
  contractNumber?: string;
  metadata?: Record<string, any>;
}

interface CarrierStats {
  carrierName: string;
  count: number; // Backend returns 'count' not 'policyCount'
  totalPremium: number;
}

export function CarriersSettings() {
  const { branding } = useBranding();
  const { toast } = useToast();
  const { user } = useUserContext();
  const searchParams = useSearchParams();
  const router = useRouter();

  // Get carrier tab from URL, default to "view"
  const carrierTab = searchParams.get("carrierTab") || "view";

  // Handle carrier tab change
  const handleCarrierTabChange = (value: string) => {
    const params = new URLSearchParams(searchParams.toString());
    params.set("tab", "carriers");
    params.set("carrierTab", value);
    router.replace(`/settings?${params.toString()}`, { scroll: false });
  };

  // Check user role and agency membership
  const adminAccess = getAdminAccess(
    user?.user?.userType || '',
    user?.currentAgency?.role
  );
  const isAgencyOwner = !!(adminAccess.isAgencyOwner || adminAccess.isAgencyAdmin);
  // Use AgencyTokenManager to get the correct agency ID (more reliable than user context)
  const agencyId = AgencyTokenManager.getCurrentAgencyId(); // Current agency ID
  // Check both agencyId from token manager AND user context to reliably detect agency membership
  const isAgencyMember = !!agencyId || !!user?.currentAgency?.agency?.id;
  const userRole = user?.currentAgency?.role;
  // Agents/viewers in an agency can never edit - only owners/admins can
  const isRegularAgent = userRole === 'agent' || userRole === 'viewer';
  const isIndependentAgent = !isAgencyMember && !isRegularAgent; // Truly independent (no agency at all)
  const canEdit = (isAgencyOwner || isIndependentAgent) && !isRegularAgent;
  const agencyName = user?.currentAgency?.agency?.name || "Agency";

  const [availableCarriers, setAvailableCarriers] = useState<Carrier[]>([]);
  const [selectedCarriers, setSelectedCarriers] = useState<Set<string>>(new Set());
  const [inheritedCarriers, setInheritedCarriers] = useState<Set<string>>(new Set()); // Locked carriers from parent agency
  const [agencyCarriers, setAgencyCarriers] = useState<AgencyCarrier[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // Request Carrier Modal State
  const [isRequestModalOpen, setIsRequestModalOpen] = useState(false);
  const [requestType, setRequestType] = useState<CarrierRequestType>("add_carrier");
  const [newCarrierName, setNewCarrierName] = useState("");
  const [requestReason, setRequestReason] = useState("");
  const [isSubmittingRequest, setIsSubmittingRequest] = useState(false);

  // PDF Upload State
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Policy Map Modal State
  const [showPolicyMap, setShowPolicyMap] = useState(false);

  // OAuth Email Connection State
  const [oauthConnection, setOauthConnection] = useState<EmailOAuthConnection | null>(null);
  const [checkingOAuthConnection, setCheckingOAuthConnection] = useState(false);

  // Carrier stats and expandable state
  const [carrierStats, setCarrierStats] = useState<CarrierStats[]>([]);
  const [expandedCarriers, setExpandedCarriers] = useState<Set<string>>(new Set());

  // Load data on mount and when agency changes
  useEffect(() => {
    fetchData();
    fetchCarrierStats();
  }, [agencyId]); // Re-fetch when agency changes

  // Check OAuth connection when modal opens
  useEffect(() => {
    if (isRequestModalOpen) {
      checkOAuthConnection();
    }
  }, [isRequestModalOpen]);

  const checkOAuthConnection = async () => {
    setCheckingOAuthConnection(true);
    try {
      const status = await oauthConnectionService.getConnectionStatus();
      const googleConnected = status.google?.status === "connected";
      const microsoftConnected = status.microsoft?.status === "connected";

      if (googleConnected) {
        setOauthConnection(status.google!);
      } else if (microsoftConnected) {
        setOauthConnection(status.microsoft!);
      } else {
        setOauthConnection(null);
      }
    } catch (error) {
      console.error("Failed to check OAuth connection:", error);
      setOauthConnection(null);
    } finally {
      setCheckingOAuthConnection(false);
    }
  };

  // File handling functions
  const handleFileSelect = (file: File) => {
    // Validate file type (PDF only)
    if (file.type !== "application/pdf") {
      toast({
        title: "Invalid File Type",
        description: "Only PDF files are allowed.",
        variant: "destructive",
      });
      return;
    }

    // Validate file size (max 10MB)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      toast({
        title: "File Too Large",
        description: "Maximum file size is 10MB.",
        variant: "destructive",
      });
      return;
    }

    setSelectedFile(file);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const file = e.dataTransfer.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const removeSelectedFile = () => {
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const fetchData = async (showLoading = true) => {
    try {
      if (showLoading) setIsLoading(true);

      // Fetch available carriers and agency's current selections in parallel
      const [availableRes, agencyRes] = await Promise.all([
        handleApiCall(async () => {
          return await api.get({
            url: getApiUrl('/agency-carriers/available')
          });
        }),
        handleApiCall(async () => {
          // For agency members, ensure we only get their agency's carriers
          const url = agencyId
            ? getApiUrl(`/agency-carriers?agencyId=${agencyId}`)
            : getApiUrl('/agency-carriers');
          return await api.get({
            url,
            auth: true,
          });
        })
      ]);

      if (availableRes) {
        setAvailableCarriers(Array.isArray(availableRes) ? availableRes : []);
      }

      if (agencyRes) {
        // Backend now correctly filters by agency, so we trust the response
        const carriers: AgencyCarrier[] = Array.isArray(agencyRes) ? agencyRes : [];

        setAgencyCarriers(carriers);
        const enabled = carriers.filter(ac => ac.isEnabled).map(ac => ac.carrierSlug);
        setSelectedCarriers(new Set(enabled));
        // Track inherited carriers from parent agencies (these are locked and cannot be removed)
        const inherited = carriers
          .filter(ac => ac.isEnabled && ac.metadata?.inherited)
          .map(ac => ac.carrierSlug);
        setInheritedCarriers(new Set(inherited));
      }
    } catch (error) {

      toast({
        title: "Error",
        description: "Failed to load carriers. Please try again.",
        variant: "destructive",
      });
    } finally {
      if (showLoading) setIsLoading(false);
    }
  };

  const fetchCarrierStats = async () => {
    try {
      // handleApiCall already extracts the 'data' field from the response
      const stats = await handleApiCall<CarrierStats[]>(async () => {
        return await api.get({
          url: getApiUrl('/policies/stats/carriers'),
          auth: true,
        });
      });

      if (stats && Array.isArray(stats)) {
        setCarrierStats(stats);
      }
    } catch (error) {
      // Silent fail - stats are optional
      console.error('Failed to fetch carrier stats:', error);
    }
  };

  const toggleCarrierExpanded = (carrierId: string) => {
    setExpandedCarriers(prev => {
      const newSet = new Set(prev);
      if (newSet.has(carrierId)) {
        newSet.delete(carrierId);
      } else {
        newSet.add(carrierId);
      }
      return newSet;
    });
  };

  const getCarrierPolicyCount = (carrierName: string): number => {
    const stat = carrierStats.find(s =>
      s.carrierName?.toLowerCase() === carrierName?.toLowerCase()
    );
    return Number(stat?.count) || 0;
  };

  const getCarrierTotalPremium = (carrierName: string): number => {
    const stat = carrierStats.find(s =>
      s.carrierName?.toLowerCase() === carrierName?.toLowerCase()
    );
    return Number(stat?.totalPremium) || 0;
  };

  // Get total policies sold across all carriers
  const getTotalPoliciesSold = (): number => {
    return carrierStats.reduce((sum, stat) => sum + (Number(stat.count) || 0), 0);
  };

  const handleToggleCarrier = (slug: string) => {
    if (!canEdit) {
      toast({
        title: "Permission Denied",
        description: "You cannot modify carrier selections. Only agency owners can make changes.",
        variant: "destructive",
      });
      return;
    }

    // Inherited carriers from parent agencies cannot be removed
    if (inheritedCarriers.has(slug)) {
      toast({
        title: "Inherited Carrier",
        description: "This carrier is inherited from a parent agency and cannot be removed.",
      });
      return;
    }

    setSelectedCarriers(prev => {
      const newSet = new Set(prev);
      if (newSet.has(slug)) {
        newSet.delete(slug);
      } else {
        newSet.add(slug);
      }
      setHasChanges(true);
      return newSet;
    });
  };

  const handleSelectAll = () => {
    if (!canEdit) return;

    const filtered = getFilteredCarriers();
    setSelectedCarriers(new Set(filtered.map(c => c.slug)));
    setHasChanges(true);
  };

  const handleDeselectAll = () => {
    if (!canEdit) return;

    // Keep inherited carriers selected (they can't be removed)
    setSelectedCarriers(new Set(inheritedCarriers));
    setHasChanges(true);
  };

  const handleSave = async () => {
    if (!canEdit) return;

    // Optimistic update - immediately update UI before API call
    const selectedSlugs = Array.from(selectedCarriers);
    const optimisticCarriers: AgencyCarrier[] = selectedSlugs.map(slug => {
      const carrier = availableCarriers.find(c => c.slug === slug);
      return {
        agencyId: agencyId || '',
        carrierId: carrier?.id || '',
        carrierSlug: slug,
        carrierName: carrier?.name || slug,
        isEnabled: true,
        displayOrder: 0,
        isDefault: false,
      };
    });

    // Immediately update the state for instant UI feedback
    setAgencyCarriers(optimisticCarriers);
    setHasChanges(false);

    // Show success toast immediately
    toast({
      title: "Success",
      description: `Updated ${selectedCarriers.size} carrier selections.`,
    });

    // API call in background (no loading state shown to user)
    try {
      await handleApiCall(async () => {
        return await api.post({
          url: getApiUrl('/agency-carriers/bulk'),
          body: {
            carrierSlugs: selectedSlugs,
            isEnabled: true,
          },
          auth: true,
        });
      });

      // Silently refresh data in background to sync with server (no loading indicator)
      fetchData(false);
      fetchCarrierStats();
    } catch (error: any) {
      // On error, revert and show error
      toast({
        title: "Error",
        description: error.response?.data?.message || "Failed to save carriers. Please try again.",
        variant: "destructive",
      });
      // Revert by fetching original data (no loading indicator)
      fetchData(false);
      setHasChanges(true);
    }
  };

  const handleCancel = () => {
    fetchData(true); // Reset to original (show loading since it's a user action)
    setHasChanges(false);
  };

  const handleRequestCarrier = async () => {
    if (!newCarrierName.trim()) {
      toast({
        title: "Validation Error",
        description: "Please enter a carrier name.",
        variant: "destructive",
      });
      return;
    }

    if (!requestReason.trim()) {
      toast({
        title: "Validation Error",
        description: "Please provide a reason for your request.",
        variant: "destructive",
      });
      return;
    }

    // For underwriting data requests with PDF, require OAuth connection
    if (requestType === "underwriting_data" && selectedFile && !oauthConnection) {
      toast({
        title: "Email Connection Required",
        description: "Please connect your Gmail or Outlook account to send underwriting documents.",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsSubmittingRequest(true);

      // Create FormData for file upload
      const formData = new FormData();
      formData.append("carrierName", newCarrierName.trim());
      formData.append("requestReason", requestReason.trim());
      formData.append("requestType", requestType);

      // Add file if present (for underwriting data requests)
      if (selectedFile) {
        formData.append("file", selectedFile);
      }

      // Add OAuth provider info if connected (for sending via user's email)
      if (oauthConnection && selectedFile) {
        formData.append("useOAuth", "true");
        formData.append("oauthProvider", oauthConnection.provider);
      }

      const response = await handleApiCall(async () => {
        return await api.post({
          url: getApiUrl('/carrier-requests/with-attachment'),
          body: formData,
          auth: true,
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
      });

      if (response) {
        const successMessage = requestType === "add_carrier"
          ? `Your request to add "${newCarrierName}" has been submitted successfully.`
          : `Your underwriting data request for "${newCarrierName}" has been submitted${selectedFile ? " with the attached PDF" : ""}.`;

        toast({
          title: "Request Submitted",
          description: successMessage,
        });

        // Reset form and close modal
        resetRequestForm();
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.response?.data?.message || "Failed to submit carrier request. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmittingRequest(false);
    }
  };

  const resetRequestForm = () => {
    setNewCarrierName("");
    setRequestReason("");
    setSelectedFile(null);
    setRequestType("add_carrier");
    setIsRequestModalOpen(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const getFilteredCarriers = () => {
    if (!searchTerm.trim()) return availableCarriers;

    const searchLower = searchTerm.toLowerCase();
    return availableCarriers.filter(carrier =>
      carrier.name.toLowerCase().includes(searchLower) ||
      carrier.slug.toLowerCase().includes(searchLower)
    );
  };

  const filteredCarriers = getFilteredCarriers();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-primary" />
          <p className="text-muted-foreground">Loading carriers...</p>
        </div>
      </div>
    );
  }

  // Get enabled carriers for display - only show agency's enabled carriers
  // For agents, ensure they only see carriers from their agency that are enabled
  const enabledCarriers = agencyCarriers.filter(ac => {
    // Must be enabled
    if (!ac.isEnabled) return false;

    // For agency members, ensure it's from their agency
    if (isAgencyMember && agencyId) {
      return ac.agencyId === agencyId || !ac.agencyId; // Include if matches agency or no agencyId specified
    }

    // For independent agents, show all enabled carriers (they have no agencyId or null agencyId)
    if (isIndependentAgent) {
      return true; // Independent agents see all their enabled carriers
    }

    // For independent agents or owners, show all enabled
    return true;
  });

  // For agents who can't edit, show only the view content without tabs UI
  if (isAgencyMember && !canEdit) {
    return (
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <div className="flex items-start justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="h-5 w-5" />
                  {agencyName} Carriers
                </CardTitle>
                <CardDescription>
                  Carriers configured for your agency. {enabledCarriers.length === 0 ? 'No carriers selected yet.' : `${enabledCarriers.length} carrier${enabledCarriers.length === 1 ? '' : 's'} available.`}
                </CardDescription>
              </div>
              {/* Total Policies Badge - Top Right */}
              <div className="flex flex-col items-end gap-1">
                <p className="text-[10px] text-gray-500 uppercase tracking-wide">Your Policies Sold</p>
                <div className="px-3 py-2 bg-primary/5 border border-primary/30 rounded-lg">
                  <div className="flex items-center justify-center gap-1.5">
                    <FileText className="h-4 w-4 text-primary" />
                    <p className="text-lg font-bold text-primary">{getTotalPoliciesSold()}</p>
                  </div>
                  <p className="text-xs text-primary text-center">Policies Sold</p>
                </div>
              </div>
            </div>
          </CardHeader>

          <CardContent>
            {enabledCarriers.length === 0 ? (
              <div className="text-center py-12">
                <Building2 className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                <p className="text-lg font-medium text-gray-600 mb-2">No Carriers Selected</p>
                <p className="text-sm text-muted-foreground">
                  Contact your agency owner to configure carriers.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 items-start">
                {enabledCarriers.map((carrier) => {
                  // Use carrierSlug as unique identifier (carrierId might be undefined)
                  const uniqueId = carrier.carrierSlug || carrier.carrierId;
                  const isExpanded = expandedCarriers.has(uniqueId);
                  const policyCount = getCarrierPolicyCount(carrier.carrierName);
                  const totalPremium = getCarrierTotalPremium(carrier.carrierName);

                  return (
                    <div
                      key={uniqueId}
                      className="group relative bg-gradient-to-br from-white to-gray-50 border border-gray-200 rounded-xl overflow-hidden hover:shadow-md hover:border-primary/30 transition-all duration-200"
                    >
                      <button
                        onClick={() => toggleCarrierExpanded(uniqueId)}
                        className="w-full text-left p-4 min-h-[88px]"
                      >
                        <div className="flex items-start gap-3">
                          <div className="flex-shrink-0 w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                            <Building2 className="h-5 w-5 text-primary" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4 className="font-medium text-sm text-gray-900 leading-tight line-clamp-2 min-h-[40px]">
                              {carrier.carrierName}
                            </h4>
                            <div className="flex items-center gap-2 mt-2">
                              <span className="inline-flex items-center gap-1 text-xs text-green-600 font-medium">
                                <span className="w-1.5 h-1.5 bg-green-500 rounded-full"></span>
                                Active
                              </span>
                              {carrier.isDefault && (
                                <Badge variant="secondary" className="text-[10px] px-1.5 py-0.5 bg-primary/10 text-primary border-0">
                                  Default
                                </Badge>
                              )}
                            </div>
                          </div>
                          <div className="flex-shrink-0">
                            {isExpanded ? (
                              <ChevronUp className="h-4 w-4 text-gray-400" />
                            ) : (
                              <ChevronDown className="h-4 w-4 text-gray-400" />
                            )}
                          </div>
                        </div>
                      </button>

                      {/* Expanded Stats Section */}
                      {isExpanded && (
                        <div className="px-4 pb-4 pt-0 border-t border-gray-100 bg-gray-50/50">
                          <div className="mt-3 space-y-2">
                            <div className="flex items-center justify-between text-sm">
                              <span className="text-gray-500 flex items-center gap-1.5">
                                <FileText className="h-3.5 w-3.5" />
                                Your Policies Sold
                              </span>
                              <span className="font-semibold text-gray-900">{policyCount}</span>
                            </div>
                            {policyCount > 0 && (
                              <div className="flex items-center justify-between text-sm">
                                <span className="text-gray-500">Total Premium</span>
                                <span className="font-semibold text-green-600">
                                  ${totalPremium.toLocaleString()}
                                </span>
                              </div>
                            )}
                            {policyCount === 0 && (
                              <p className="text-xs text-gray-400 italic">No policies sold yet</p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // For owners/independent agents, show tabs with both manage and view options
  return (
    <div className="space-y-6">
      <Tabs value={carrierTab} onValueChange={handleCarrierTabChange} className="space-y-6">
        <TabsList className="m-subtabs-fill inline-flex w-full">
          <TabsTrigger value="view" className="flex-1 flex items-center gap-2">
            <Eye className="h-4 w-4" />
            {isAgencyMember ? `${agencyName} Carriers` : 'My Carriers'}
          </TabsTrigger>
          <TabsTrigger value="manage" className="flex-1 flex items-center gap-2">
            <Settings className="h-4 w-4" />
            Manage Carriers
          </TabsTrigger>
        </TabsList>

        {/* Manage Carriers Tab - For owners/independent agents */}
        {canEdit && (
          <TabsContent value="manage">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Building2 className="h-5 w-5" />
                      Carrier Selection
                    </CardTitle>
                    <CardDescription>
                      {isIndependentAgent
                        ? 'Select carriers you work with for policy management and lead tracking.'
                        : 'Select carriers your agency works with. These will be available to all agents.'
                      }
                    </CardDescription>
                  </div>

                  {hasChanges && (
                    <div className="flex items-center gap-2">
                      <Button
                        onClick={handleCancel}
                        variant="outline"
                        size="sm"
                        disabled={isSaving}
                      >
                        <X className="h-4 w-4 mr-2" />
                        Cancel
                      </Button>
                      <Button
                        onClick={handleSave}
                        size="sm"
                        disabled={isSaving}
                        className="bg-primary hover:bg-primary/90"
                      >
                        <Save className="h-4 w-4 mr-2" />
                        {isSaving ? 'Saving...' : `Save Changes`}
                      </Button>
                    </div>
                  )}
                </div>
              </CardHeader>

        <CardContent className="space-y-4">
          {!canEdit && isAgencyMember && (
            <Alert>
              <AlertDescription>
                You can view but cannot modify carrier selections. Only agency owners can make changes.
              </AlertDescription>
            </Alert>
          )}

          {/* Stats Bar */}
          <div className="flex items-center gap-4 p-4 bg-primary/5 rounded-lg border border-primary/30">
            <div>
              <p className="text-2xl font-bold text-gray-900">{selectedCarriers.size}</p>
              <p className="text-xs text-primary">Selected</p>
            </div>
            <div className="h-8 w-px bg-primary/30" />
            <div>
              <p className="text-2xl font-bold text-gray-700">{availableCarriers.length}</p>
              <p className="text-xs text-muted-foreground">Total Available</p>
            </div>
            {searchTerm && (
              <>
                <div className="h-8 w-px bg-primary/30" />
                <div>
                  <p className="text-2xl font-bold text-gray-700">{filteredCarriers.length}</p>
                  <p className="text-xs text-muted-foreground">Matching Search</p>
                </div>
              </>
            )}
          </div>

          {/* Search Bar */}
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search carriers by name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>

            {canEdit && (
              <div className="flex gap-2">
                <Button
                  onClick={handleSelectAll}
                  variant="outline"
                  size="sm"
                  disabled={isSaving}
                >
                  Select All {searchTerm && '(Filtered)'}
                </Button>
                <Button
                  onClick={handleDeselectAll}
                  variant="outline"
                  size="sm"
                  disabled={isSaving}
                >
                  Clear All
                </Button>
              </div>
            )}
          </div>

          {/* Carriers List */}
          <ScrollArea className="h-[500px] rounded-md border">
            <div className="p-4 space-y-2">
              {filteredCarriers.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-muted-foreground">
                    {searchTerm ? 'No carriers found matching your search.' : 'No carriers available.'}
                  </p>
                </div>
              ) : (
                filteredCarriers.map((carrier) => {
                  const isSelected = selectedCarriers.has(carrier.slug);
                  const isInherited = inheritedCarriers.has(carrier.slug);

                  return (
                    <div
                      key={carrier.id}
                      onClick={() => handleToggleCarrier(carrier.slug)}
                      className={`flex items-center gap-3 p-3 rounded-lg border-2 transition-all ${
                        isInherited
                          ? 'border-primary/40 bg-primary/5 cursor-not-allowed opacity-80'
                          : isSelected
                            ? 'border-primary bg-primary/5'
                            : 'border-gray-200 bg-white hover:border-primary/30'
                      } ${canEdit && !isInherited ? 'cursor-pointer' : isInherited ? '' : 'cursor-default'}`}
                    >
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={() => handleToggleCarrier(carrier.slug)}
                        disabled={!canEdit || isSaving || isInherited}
                        onClick={(e) => e.stopPropagation()}
                      />

                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{carrier.name}</p>
                      </div>

                      {isInherited ? (
                        <Badge variant="outline" className="text-[10px] px-1.5 py-0.5 border-primary/30 text-primary flex-shrink-0">
                          Inherited
                        </Badge>
                      ) : isSelected ? (
                        <Check className="h-5 w-5 text-primary flex-shrink-0" />
                      ) : null}
                    </div>
                  );
                })
              )}
            </div>
          </ScrollArea>

          {/* Request Carrier Button - Prominent Red */}
          <div className="mt-4">
            <Button
              onClick={() => setIsRequestModalOpen(true)}
              variant="destructive"
              className="w-full py-6 text-base font-semibold bg-red-600 hover:bg-red-700 shadow-md hover:shadow-lg transition-all"
            >
              <Plus className="h-5 w-5 mr-2" />
              Request Carrier Not Listed
            </Button>
          </div>

          {/* Selected Carriers Summary */}
          {selectedCarriers.size > 0 && (
            <div className="mt-4 p-4 bg-primary/5 border border-primary/30 rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-gray-900">
                  Your Selected Carriers ({selectedCarriers.size})
                </h3>
              </div>
              <div className="flex flex-wrap gap-2">
                {Array.from(selectedCarriers).map((slug) => {
                  const carrier = availableCarriers.find(c => c.slug === slug);
                  if (!carrier) return null;
                  const isInherited = inheritedCarriers.has(slug);

                  return (
                    <div
                      key={slug}
                      className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium ${
                        isInherited
                          ? 'bg-primary/70 text-white'
                          : 'bg-primary text-white'
                      }`}
                    >
                      <span>{carrier.name}</span>
                      {isInherited ? (
                        <span className="text-[10px] opacity-80">Inherited</span>
                      ) : canEdit ? (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleToggleCarrier(slug);
                          }}
                          className="hover:bg-primary/90 rounded-full p-0.5 transition-colors"
                          disabled={isSaving}
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

              {/* Footer Note */}
              {isAgencyOwner && (
                <Alert className="bg-amber-50 border-amber-200">
                  <AlertDescription className="text-sm">
                    <strong>Note:</strong> If you have sub-agencies, they will inherit these carriers by default. Sub-agencies can add additional carriers but cannot remove inherited ones.
                  </AlertDescription>
                </Alert>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        )}

        {/* View Carriers Tab - For all users (agency members and independent agents) */}
        <TabsContent value="view">
          <Card>
            <CardHeader>
              <div className="carriers-view-header flex items-start justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Building2 className="h-5 w-5" />
                    {isAgencyMember ? `${agencyName} Carriers` : 'My Carriers'}
                  </CardTitle>
                  <CardDescription>
                    {isAgencyMember
                      ? `Carriers configured for your agency. ${enabledCarriers.length === 0 ? 'No carriers selected yet.' : `${enabledCarriers.length} carrier${enabledCarriers.length === 1 ? '' : 's'} available.`}`
                      : `Your selected carriers. ${enabledCarriers.length === 0 ? 'No carriers selected yet.' : `${enabledCarriers.length} carrier${enabledCarriers.length === 1 ? '' : 's'} available.`}`
                    }
                  </CardDescription>
                </div>
                <Button variant="outline" size="sm" onClick={() => setShowPolicyMap(!showPolicyMap)}>
                  <Map className="h-4 w-4 mr-1.5" />
                  {showPolicyMap ? "Hide Map" : "Policy Map"}
                </Button>
              </div>
            </CardHeader>

            <CardContent>
              <PolicyMapInline open={showPolicyMap} />
              {enabledCarriers.length === 0 ? (
                <div className="text-center py-12">
                  <Building2 className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                  <p className="text-lg font-medium text-gray-600 mb-2">No Carriers Selected</p>
                  <p className="text-sm text-muted-foreground">
                    {isIndependentAgent
                      ? 'Get started by selecting carriers from the Manage Carriers tab.'
                      : isAgencyOwner
                        ? 'Get started by selecting carriers from the Manage Carriers tab.'
                        : 'Contact your agency owner to configure carriers.'}
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 items-start">
                  {enabledCarriers.map((carrier) => {
                    // Use carrierSlug as unique identifier (carrierId might be undefined)
                    const uniqueId = carrier.carrierSlug || carrier.carrierId;
                    const isExpanded = expandedCarriers.has(uniqueId);
                    const policyCount = getCarrierPolicyCount(carrier.carrierName);
                    const totalPremium = getCarrierTotalPremium(carrier.carrierName);

                    return (
                      <div
                        key={uniqueId}
                        className="group relative bg-gradient-to-br from-white to-gray-50 border border-gray-200 rounded-xl overflow-hidden hover:shadow-md hover:border-primary/30 transition-all duration-200"
                      >
                        <button
                          onClick={() => toggleCarrierExpanded(uniqueId)}
                          className="w-full text-left p-4 min-h-[88px]"
                        >
                          <div className="flex items-start gap-3">
                            <div className="flex-shrink-0 w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                              <Building2 className="h-5 w-5 text-primary" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <h4 className="font-medium text-sm text-gray-900 leading-tight line-clamp-2 min-h-[40px]">
                                {carrier.carrierName}
                              </h4>
                              <div className="flex items-center gap-2 mt-2">
                                <span className="inline-flex items-center gap-1 text-xs text-green-600 font-medium">
                                  <span className="w-1.5 h-1.5 bg-green-500 rounded-full"></span>
                                  Active
                                </span>
                                {carrier.isDefault && (
                                  <Badge variant="secondary" className="text-[10px] px-1.5 py-0.5 bg-primary/10 text-primary border-0">
                                    Default
                                  </Badge>
                                )}
                              </div>
                            </div>
                            <div className="flex-shrink-0">
                              {isExpanded ? (
                                <ChevronUp className="h-4 w-4 text-gray-400" />
                              ) : (
                                <ChevronDown className="h-4 w-4 text-gray-400" />
                              )}
                            </div>
                          </div>
                        </button>

                        {/* Expanded Stats Section */}
                        {isExpanded && (
                          <div className="px-4 pb-4 pt-0 border-t border-gray-100 bg-gray-50/50">
                            <div className="mt-3 space-y-2">
                              <div className="flex items-center justify-between text-sm">
                                <span className="text-gray-500 flex items-center gap-1.5">
                                  <FileText className="h-3.5 w-3.5" />
                                  {isAgencyOwner ? 'Agency Policies Sold' : 'Your Policies Sold'}
                                </span>
                                <span className="font-semibold text-gray-900">{policyCount}</span>
                              </div>
                              {policyCount > 0 && (
                                <div className="flex items-center justify-between text-sm">
                                  <span className="text-gray-500">Total Premium</span>
                                  <span className="font-semibold text-green-600">
                                    ${totalPremium.toLocaleString()}
                                  </span>
                                </div>
                              )}
                              {policyCount === 0 && (
                                <p className="text-xs text-gray-400 italic">No policies sold yet</p>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Enhanced 2-in-1 Request Carrier Modal */}
      <Dialog open={isRequestModalOpen} onOpenChange={(open) => {
        if (!open) resetRequestForm();
        setIsRequestModalOpen(open);
      }}>
        <ResponsiveDialogContent size="lg" className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5 text-primary" />
              Carrier Request
            </DialogTitle>
            <DialogDescription>
              Request a new carrier to be added or submit underwriting data for our AI system.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Request Type Selection */}
            <div className="space-y-3">
              <Label className="text-sm font-medium">What would you like to do?</Label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setRequestType("add_carrier")}
                  disabled={isSubmittingRequest}
                  className={`p-4 rounded-lg border-2 text-left transition-all ${
                    requestType === "add_carrier"
                      ? "border-primary bg-primary/5"
                      : "border-gray-200 hover:border-primary/30 bg-white"
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`p-2 rounded-lg ${requestType === "add_carrier" ? "bg-primary/10" : "bg-gray-100"}`}>
                      <Plus className={`h-5 w-5 ${requestType === "add_carrier" ? "text-primary" : "text-gray-500"}`} />
                    </div>
                    <div>
                      <p className="font-medium text-sm">Add New Carrier</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Request a carrier to be added to our platform
                      </p>
                    </div>
                  </div>
                </button>

                <button
                  type="button"
                  onClick={() => setRequestType("underwriting_data")}
                  disabled={isSubmittingRequest}
                  className={`p-4 rounded-lg border-2 text-left transition-all ${
                    requestType === "underwriting_data"
                      ? "border-primary bg-primary/5"
                      : "border-gray-200 hover:border-primary/30 bg-white"
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`p-2 rounded-lg ${requestType === "underwriting_data" ? "bg-primary/10" : "bg-gray-100"}`}>
                      <FileText className={`h-5 w-5 ${requestType === "underwriting_data" ? "text-primary" : "text-gray-500"}`} />
                    </div>
                    <div>
                      <p className="font-medium text-sm">Submit Underwriting Data</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Upload carrier underwriting guidelines for InsuraBot
                      </p>
                    </div>
                  </div>
                </button>
              </div>
            </div>

            {/* Carrier Name Input */}
            <div className="space-y-2">
              <Label htmlFor="newCarrierName">
                Carrier Name <span className="text-red-500">*</span>
              </Label>
              <Input
                id="newCarrierName"
                placeholder="ABC Life Insurance"
                value={newCarrierName}
                onChange={(e) => setNewCarrierName(e.target.value)}
                disabled={isSubmittingRequest}
              />
            </div>

            {/* Reason/Notes Input */}
            <div className="space-y-2">
              <Label htmlFor="requestReason">
                {requestType === "add_carrier" ? "Reason for Request" : "Additional Notes"} <span className="text-red-500">*</span>
              </Label>
              <Textarea
                id="requestReason"
                placeholder={requestType === "add_carrier"
                  ? "Tell us why you need this carrier and how you plan to use it..."
                  : "Describe the underwriting data you're submitting (e.g., health conditions covered, age limits, etc.)..."
                }
                value={requestReason}
                onChange={(e) => setRequestReason(e.target.value)}
                disabled={isSubmittingRequest}
                className="min-h-[100px]"
              />
            </div>

            {/* PDF Upload Section - Only for underwriting data */}
            {requestType === "underwriting_data" && (
              <div className="space-y-3">
                <Label className="text-sm font-medium">
                  Upload Underwriting PDF <span className="text-muted-foreground">(Optional)</span>
                </Label>

                {/* OAuth Connection Status */}
                {checkingOAuthConnection ? (
                  <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                    <Loader2 className="h-4 w-4 animate-spin text-gray-500" />
                    <span className="text-sm text-gray-500">Checking email connection...</span>
                  </div>
                ) : oauthConnection ? (
                  <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                    <Mail className="h-4 w-4 text-green-600" />
                    <span className="text-sm text-green-700">
                      Connected via {oauthConnection.provider === "google" ? "Gmail" : "Outlook"}: {oauthConnection.email}
                    </span>
                  </div>
                ) : (
                  <Alert className="bg-amber-50 border-amber-200">
                    <AlertCircle className="h-4 w-4 text-amber-600" />
                    <AlertDescription className="text-sm text-amber-800">
                      <strong>Email not connected.</strong> To upload PDFs, please{" "}
                      <button
                        type="button"
                        onClick={() => router.push("/settings?tab=account&accountTab=connections")}
                        className="text-primary hover:underline inline-flex items-center gap-1"
                      >
                        <Link2 className="h-3 w-3" />
                        connect your Gmail or Outlook
                      </button>{" "}
                      in Account settings. The PDF will be sent to our team via your connected email.
                    </AlertDescription>
                  </Alert>
                )}

                {/* File Upload Drop Zone - Only show if OAuth is connected */}
                {oauthConnection && (
                  <>
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleFileInputChange}
                      accept=".pdf,application/pdf"
                      className="hidden"
                      disabled={isSubmittingRequest}
                    />

                    {!selectedFile ? (
                      <div
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                        onClick={() => fileInputRef.current?.click()}
                        className={`relative border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-all ${
                          isDragging
                            ? "border-primary bg-primary/5"
                            : "border-gray-300 hover:border-primary/30 hover:bg-gray-50"
                        }`}
                      >
                        <Upload className={`h-8 w-8 mx-auto mb-2 ${isDragging ? "text-primary" : "text-gray-400"}`} />
                        <p className="text-sm font-medium text-gray-700">
                          {isDragging ? "Drop PDF here" : "Click to upload or drag and drop"}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">PDF only, max 10MB</p>
                      </div>
                    ) : (
                      <div className="flex items-center gap-3 p-4 bg-primary/5 border border-primary/30 rounded-lg">
                        <div className="flex-shrink-0 w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                          <FileText className="h-5 w-5 text-primary" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm text-gray-900 truncate">{selectedFile.name}</p>
                          <p className="text-xs text-gray-500">
                            {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                          </p>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            removeSelectedFile();
                          }}
                          disabled={isSubmittingRequest}
                          className="text-gray-500 hover:text-red-600"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    )}

                    <p className="text-xs text-muted-foreground">
                      The PDF will be sent to {branding.supportEmail} via your connected email for review and virus scanning.
                    </p>
                  </>
                )}
              </div>
            )}

            {/* Info Alert */}
            <Alert className="bg-primary/5 border-primary/30">
              <AlertDescription className="text-sm text-gray-800">
                {requestType === "add_carrier"
                  ? "Our team will review your request and add the carrier to the platform. You'll be notified once it's available."
                  : `Submitted underwriting data will be reviewed, scanned for security, and added to ${"InsuraBot"}'s knowledge base to help all agents.`}
              </AlertDescription>
            </Alert>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={resetRequestForm}
              disabled={isSubmittingRequest}
            >
              Cancel
            </Button>
            <Button
              onClick={handleRequestCarrier}
              disabled={isSubmittingRequest || !newCarrierName.trim() || !requestReason.trim()}
              className="bg-primary hover:bg-primary/90"
            >
              {isSubmittingRequest ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  {requestType === "add_carrier" ? (
                    <Plus className="h-4 w-4 mr-2" />
                  ) : (
                    <Mail className="h-4 w-4 mr-2" />
                  )}
                  Submit Request
                </>
              )}
            </Button>
          </DialogFooter>
        </ResponsiveDialogContent>
      </Dialog>

    </div>
  );
}
